import React, { Component, useRef } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-hot-toast";
import Header from "../../components/Header/Header";
import AutomationDashboard from "./AutomationDashboard";
import PayrollUploadModal from "./PayrollUploadModal";
import styles from "./AutomationDashboardTab.module.scss";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import GetAppIcon from "@mui/icons-material/GetApp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CommonApi from "../../api/CommonApi";
import Upload from "../../assets/images/upload.svg";
import Tooltip from "@mui/material/Tooltip";
import moment from "moment";


class AutomationDashboardTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selected: "automation",
      startDate: null,
      endDate: null,
      tabsData: { automation: [] },
      isModalOpen: false,
      isTemplateLoading: false,
      isExporting: false,
      isUploading: false,
      pendingUuids: [],
    };
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    if (this.statusInterval) clearInterval(this.statusInterval);
    if (this.exportTimeout) clearTimeout(this.exportTimeout);
  }

  handleBeforeUnload = (e) => {
    if (this.state.isUploading || this.state.isExporting) {
      e.preventDefault();
      e.returnValue =
        "If you reload, auto-refresh will not happen and you may have to refresh manually to see the latest data.";
      return e.returnValue;
    }
  };

  startBulkStatusPoller = () => {
    // If interval is already running, don't create a new one
    if (this.statusInterval) {
      this.pollAttempts = 0; // Reset attempts whenever new items are added to poller
      return;
    }

    this.pollAttempts = 0;
    const maxAttempts = 12;

    this.statusInterval = setInterval(async () => {
      this.pollAttempts++;
      const { pendingUuids } = this.state;
      
      if (pendingUuids.length === 0 || this.pollAttempts > maxAttempts) {
        if (this.pollAttempts > maxAttempts && pendingUuids.length > 0) {
          toast.error("Poller timed out, some statuses may be outdated. Please refresh.");
          this.setState({ isUploading: false, pendingUuids: [] });
        }
        this.stopPoller();
        return;
      }

      try {
        const response = await CommonApi.getAutomationBatchStatus({ uuids: pendingUuids });
        
        if (response.success && response.results) {
          const batchResults = response.results;
          
          batchResults.forEach(item => {
              const mappedItem = {
                  uploadDate: item.uploadedTimestamp ? moment(Number(item.uploadedTimestamp)).format("M/D/YY") : "-",
                  clientId: item.client_id || "-",
                  username: item.username || "-",
                  fileName: item.fileName || "N/A",
                  s3FilePath: item.s3_file_path,
                  status: item.status,
                  invoicePath: item.invoicePath,
                  id: item.uuid, // used for matching rows
                  InvoiceGenerationError: item.InvoiceGenerationError,
                  lastCheckDate: item.lastCheckDate || "-",
                  validationErrors: item.validationErrors,
              };

              // This replaces the specific row without a full refresh
              this.updateTabRowData("automation", (oldData) => {
                  let exists = false;
                  const updatedData = oldData.map(row => {
                      if (row.id === mappedItem.id) {
                          exists = true;
                          return mappedItem;
                      }
                      return row;
                  });
                  if (!exists) {
                      updatedData.unshift(mappedItem);
                  }
                  return updatedData;
              });
          });

          // 2. Filter out items that are finished (Success or Failure)
          const finishedUuids = batchResults
            .filter(item => item.status !== "In Progress")
            .map(item => item.uuid);

          if (finishedUuids.length > 0) {
            this.setState(prevState => ({
              pendingUuids: prevState.pendingUuids.filter(id => !finishedUuids.includes(id))
            }), () => {
              // If the queue is now empty, turn off the loading state
              if (this.state.pendingUuids.length === 0) {
                this.setState({ isUploading: false });
                this.stopPoller();
              }
            });
          }
        }
      } catch (err) {
        console.error("Batch Polling error:", err);
      }
    }, 5000);
  };

  stopPoller = () => {
    if (this.statusInterval) {
      clearInterval(this.statusInterval);
      this.statusInterval = null;
    }
  };

  handleModalSubmit = async (enrichedFiles) => {
    const username = this.props.sessionData.attributes.name;
    this.setState({ isUploading: true });

  const validatedFilesMetadata = enrichedFiles.map(item => ({
    fileName: item.file.name,
    contentType: item.file.type,
    clientId: item.clientId,
    lastCheckDate: item.lastCheckDate,
  }));


    try {
      const batchResponse = await CommonApi.getBatchSignedUrls(
        validatedFilesMetadata.map((f) => ({
          fileName: f.fileName,
          contentType: f.contentType,
        })),
      );

      if (!batchResponse.success) {
        this.setState({ isUploading: false });
        return;
      }
      const uploadResults = await Promise.allSettled(
        enrichedFiles.map(async (item, index) => {
          const fileInfo = batchResponse.urls[index];
          const meta = validatedFilesMetadata[index];

          const uploadResponse = await fetch(fileInfo.uploadUrl, {
            method: "PUT",
            body: item.file,
          });
          if (!uploadResponse.ok) throw new Error(item.file.name);

          return {
            uuid: fileInfo.uuid,
            s3Key: fileInfo.key,
            fileName: item.file.name,
            clientId: meta.clientId,
            lastCheckDate: meta.lastCheckDate,
            username,
          };
        }),
      );

      const successfulEntries = uploadResults
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value);

      const failedCount = uploadResults.filter(
        (r) => r.status === "rejected",
      ).length;

      if (successfulEntries.length > 0) {
        const bulkResponse = await CommonApi.createAutomationEntry({
          entries: successfulEntries,
        });

        if (bulkResponse.success) {
          if (failedCount > 0)
            toast.error(`${failedCount} files failed to upload.`);

          const newUuids = successfulEntries.map((e) => e.uuid);
          
          // Generate preliminary rows for immediate UI update
          const newRows = successfulEntries.map(e => ({
              uploadDate: moment().format("M/D/YY"),
              clientId: e.clientId || "-",
              username: e.username || "-",
              fileName: e.fileName || "N/A",
              status: "In Progress",
              invoicePath: null,
              s3FilePath: e.s3Key,
              id: e.uuid,
              InvoiceGenerationError: null,
              lastCheckDate: e.lastCheckDate || "-",
              validationErrors: null,
          }));

          // Add to local state without full refresh
          this.updateTabRowData("automation", (oldData) => {
              // Prepend the new rows
              return [...newRows, ...oldData];
          });

          this.setState(
            (prevState) => ({
              pendingUuids: [...prevState.pendingUuids, ...newUuids],
              isUploading: true,
              isModalOpen: false
            }),
            () => this.startBulkStatusPoller() // start/restart poller with updated queue
          );
        } else {
          this.setState({ isUploading: false });
        }
      } else {
        this.setState({ isUploading: false });
      }

      this.setState({ isModalOpen: false });
    } catch (err) {
      console.error("Batch process error:", err);
      this.setState({ isUploading: false });
      toast.error("An error occurred during upload.");
    }
  };
updateTabRowData = (tab, newData) => {
  this.setState((prevState) => {
    const currentTabData = prevState.tabsData[tab] || [];
    const processedData = typeof newData === "function" 
        ? newData(currentTabData) 
        : newData;

    return {
      tabsData: {
        ...prevState.tabsData,
        [tab]: processedData,
      },
    };
  });
};
  handleTemplateDownload = async () => {
    try {
      this.setState({ isTemplateLoading: true });
      const res = await CommonApi.getS3SignedUrl({
        s3Path: "template/Template File for Invoice Genration Task.xlsx",
      });
      const downloadURL = res?.url || res?.signedUrl || res?.downloadUrl;
      if (downloadURL) {
        const response = await fetch(downloadURL);
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute(
          "download",
          "Template File for Invoice Genration Task.xlsx",
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        throw new Error("Template URL not found");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error downloading template");
    } finally {
      this.setState({ isTemplateLoading: false });
    }
  };

  handleExportEgnyte = async () => {
    const { startDate, endDate } = this.state;
    if (!startDate || !endDate) {
      toast.error("Please select both From and To dates");
      return;
    }

    let toastId;
    try {
      this.setState({ isExporting: true });
      toastId = toast.loading("Initiating download...");

      const fromDate = moment(startDate).format("MM/DD/YYYY");
      const toDate = moment(endDate).format("MM/DD/YYYY");

      const response = await CommonApi.initiateEgnyteDownload({
        fromDate,
        toDate,
      });

      if (response.success && response.trigger_id) {
        const trigger_id = response.trigger_id;
        toast.loading("Processing export, please wait...", { id: toastId });

        let nextDelay = 4000;
        const maxTotalTime = 15 * 60 * 1000;
        const startTime = Date.now();

        const poll = async () => {
          if (!this.state.isExporting) return;

          const elapsedTime = Date.now() - startTime;
          if (elapsedTime > maxTotalTime) {
            toast.error("Export timed out", { id: toastId });
            this.setState({ isExporting: false });
            return;
          }

          try {
            const statusResponse = await CommonApi.getEgnyteDownloadStatus({
              trigger_id,
            });
            if (statusResponse.success) {
              if (statusResponse.status === "completed") {
                this.setState({ isExporting: false });

                if (statusResponse.downloadUrl) {
                  toast.success("File ready for download!", { id: toastId });
                  const fileResponse = await fetch(statusResponse.downloadUrl);
                  const blob = await fileResponse.blob();
                  const blobUrl = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = blobUrl;
                  const fileFromDate = moment(startDate).format("MM.DD.YYYY");
                  const fileToDate = moment(endDate).format("MM.DD.YYYY");
                  link.setAttribute(
                    "download",
                    `Egnyte File ${fileFromDate} to ${fileToDate}.xlsx`,
                  );
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(blobUrl);
                } else {
                  toast.error(
                    statusResponse.message ||
                      "No data found for the selected range",
                    { id: toastId },
                  );
                }
                return;
              } else if (statusResponse.status === "failed") {
                toast.error(statusResponse.message || "Export failed", {
                  id: toastId,
                });
                this.setState({ isExporting: false });
                return;
              }
              this.exportTimeout = setTimeout(poll, nextDelay);
              nextDelay *= 2;
            } else {
              toast.error(statusResponse.message || "Error checking status", {
                id: toastId,
              });
              this.setState({ isExporting: false });
            }
          } catch (error) {
            console.error("Polling error:", error);
            toast.error("Error while polling status", { id: toastId });
            this.setState({ isExporting: false });
          }
        };
        this.exportTimeout = setTimeout(poll, nextDelay);
      } else {
        toast.error(response.message || "Failed to initiate download", {
          id: toastId,
        });
        this.setState({ isExporting: false });
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Error initiating export", { id: toastId });
      this.setState({ isExporting: false });
    }
  };

  handleRefresh = async () => {
    if (this.childDashboard) {
      await this.childDashboard.fetchAutomationData();
    }
  };

  render() {
    const { tabsData, startDate, endDate, isModalOpen } = this.state;

    return (
      <div className={styles.dashboardContainer}>
        <div className={`d-flex bg-light ${styles.dashboardHeader}`}>
          <Header sessionData={this.props.sessionData} />
        </div>

        <div className={styles.lib_dashboard}>
          <div className={styles.topControlsRow}>
            <div className={styles.uploadZone}>
              <div className={styles.labelWithInfo}>
                Please upload the Payroll file.{" "}
              </div>
              <div className="d-flex align-items-center">
                <button
                  className={styles.chooseFileBtn}
                  onClick={() => this.setState({ isModalOpen: true })}
                >
                  <img src={Upload} alt="Upload" />
                  Upload
                </button>

                <button
                  className={styles.templateBtn}
                  onClick={this.handleTemplateDownload}
                  disabled={this.state.isTemplateLoading}
                >
                  {this.state.isTemplateLoading ? (
                    <CircularProgress
                      size={18}
                      thickness={5}
                      color="inherit"
                      className={styles.loaderIcon}
                    />
                  ) : (
                    <GetAppIcon />
                  )}
                  Reference file
                </button>
              </div>
            </div>

            <div className={styles.actionZone}>
              <div className={styles.controlGroup}>
                <div className={styles.labelWithInfo}>
                  From Check Date <span className="text-danger">*</span>{" "}
                </div>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => this.setState({ startDate: date })}
                  className={styles.customDatePicker}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/DD/YYYY"
                />
              </div>
              <div className={styles.controlGroup}>
                <div className={styles.labelWithInfo}>
                  To Check Date <span className="text-danger">*</span>{" "}
                  
                </div>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => this.setState({ endDate: date })}
                  className={styles.customDatePicker}
                  dateFormat="MM/dd/yyyy"
                  minDate={startDate}
                  placeholderText="MM/DD/YYYY"
                />
              </div>

              <div className={styles.controlGroup}>
                <div className={styles.labelWithInfo}>
                  Consolidated carrier file
                </div>
                <div className={styles.buttonGroup}>
                  <button
                    className={styles.exportBtn}
                    onClick={this.handleExportEgnyte}
                    disabled={this.state.isExporting}
                  >
                    {this.state.isExporting ? (
                      <CircularProgress
                        size={18}
                        thickness={5}
                        color="inherit"
                        className={styles.loaderIcon}
                      />
                    ) : (
                      <SaveAltIcon />
                    )}
                    Export
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="Admin__Dashboard">
            <AutomationDashboard
              tabName="automation"
              sessionData={this.props.sessionData}
              rowDataList={tabsData.automation}
              updateTabRowData={this.updateTabRowData}
              onRef={(ref) => (this.childDashboard = ref)}
            />
          </div>
        </div>
        <PayrollUploadModal
          open={isModalOpen}
          onClose={() => this.setState({ isModalOpen: false })}
          onSubmit={this.handleModalSubmit}
        />
      </div>
    );
  }
}

export default AutomationDashboardTab;